// External libraries
import React, { useEffect } from 'react';

// Internal modules
import { CI, getContent, getLocale, useMountLogging } from '@/lib';
import {
  payLaterSubmitHandler,
  payLaterTermsCheckedHandler,
  // @ts-expect-error untyped import
} from '../handlers';
import { appStore, useStoreState } from '@/store';
// @ts-expect-error untyped import
import { LazyAddress } from '../Components';
import { Button } from '@/components/Button';
import { Section } from '../StyledComponents';
import { getPayLaterHeaderContent } from './utils';
import { CountryDropdown } from '@/components/Locale';
import { formInputHandlers } from '../formInputHandlers';
import { PayIn4Illustration } from './PayIn4Illustration';
import { useHandleFormFocus } from '../hooks/useHandleFormFocus';
import {
  PrimaryResidentialAddress,
  usePrimaryResidentialAddressValue,
} from '@/components/SignupFields/PrimaryResidentialAddress';
import {
  LazyCRSForm,
  LazyKYCFields,
} from '@/components/SignupFields/Components';
import { Email, PhoneNumber } from '@/components/ContactInformation';
// @ts-expect-error untyped import
import { LazyPasswordInput } from '../../SignupFields/PasswordInput';
import { SectionHeader } from '@/components/Headings';
import { LegalDisclosures } from '@/components/SignupFields/LegalDisclosures';
import { LazyResidentialAddressInterstitial } from '@/components/Interstitials/ResidentialAddress/LazyResidentialAddress';
import { CountryCodes } from '@/generated/graphql';
import { HeadingText } from './StyledComponents';
import {
  atomicsDomain,
  atomicsFlows,
  atomicsTasks,
  atomicsViewNames,
} from '@/lib/atomics';
import { features, noop } from '@/lib';
import { GOOGLE_PAY_LATER_COUNTRIES } from '@/lib/globals/constants';
import { handleCountryChange } from '@/components/Locale/Country/shared';
import {
  logGooglePayLaterCountryOverride,
  logPayLaterFormRender,
} from '../fpti';

export const PayLaterForm = (): JSX.Element => {
  const { country } = getLocale();
  const isGooglePayLaterOnly = features.isActive('isGooglePayLaterOnly');
  CI.endWith(CI.Event.PAY_LATER_RENDER, CI.Status.Ok);

  useMountLogging('PayLaterForm');

  const onFocus = useHandleFormFocus();
  const hasBNPLAltText = country === CountryCodes.Us || isGooglePayLaterOnly;
  const primaryResidentialAddressValue = usePrimaryResidentialAddressValue();
  const isResidentialAddressOpen = useStoreState(
    ({ ui }) => ui.isResidentialAddressOpen,
  );

  const shouldShowCRSTaxCollectionSection = features.isActive(
    'shouldShowCRSTaxCollectionSection',
  );

  // We need to make sure when it is GooglePayLater, we only allow supported countries
  // to be the locale country of the user.
  // If a user locale country is not supported, we need to set the country to the merchant country.
  // If the merchant country is not supported, we need to set the country to US.
  useEffect(() => {
    const merchantCountry =
      appStore.getState().api.cartDetails.merchant.country;

    logPayLaterFormRender({
      buyerCountry: country,
      isGooglePayLaterOnly,
      merchantCountry,
    });

    if (
      isGooglePayLaterOnly &&
      !GOOGLE_PAY_LATER_COUNTRIES.includes(country as CountryCodes)
    ) {
      const toCountry =
        merchantCountry &&
        GOOGLE_PAY_LATER_COUNTRIES.includes(merchantCountry as CountryCodes)
          ? (merchantCountry as CountryCodes)
          : CountryCodes.Us;
      logGooglePayLaterCountryOverride({
        buyerCountry: country,
        merchantCountry,
        toCountry,
      });
      handleCountryChange(
        { fromCountry: country as CountryCodes, toCountry },
        noop,
      );
    }
  }, [isGooglePayLaterOnly, country]);

  return (
    <>
      <SectionHeader {...getPayLaterHeaderContent(hasBNPLAltText)} />
      {!hasBNPLAltText && <PayIn4Illustration />}
      <HeadingText level={3}>
        {getContent('sectionHeaders.accountInfo.signup.base')}
      </HeadingText>
      <form
        method="post"
        noValidate
        onSubmit={e => {
          CI.endWith(CI.Event.GUEST_XO_SIGNUP_FORM_USER_INPUT, CI.Status.Ok);
          payLaterSubmitHandler(e);
        }}
        onFocusCapture={onFocus}
      >
        <Section>
          <CountryDropdown
            allowedCountries={
              isGooglePayLaterOnly ? GOOGLE_PAY_LATER_COUNTRIES : undefined
            }
            helperTextContentKey="bnpl.payLater.onlyAvailableIn"
            shouldShowHelperText={isGooglePayLaterOnly}
          />
          <LazyAddress
            {...formInputHandlers}
            showTitle={false}
            type="billing"
          />
          <LazyKYCFields {...formInputHandlers} />
          {primaryResidentialAddressValue && (
            <PrimaryResidentialAddress value={primaryResidentialAddressValue} />
          )}
          {shouldShowCRSTaxCollectionSection && <LazyCRSForm />}
          <PhoneNumber {...formInputHandlers} />
          <Email {...formInputHandlers} />
          <LazyPasswordInput {...formInputHandlers} />
          <LegalDisclosures
            {...formInputHandlers}
            changeHandler={payLaterTermsCheckedHandler}
          />
        </Section>
        <Button
          contentKey="buttons.signup.base"
          isFormSubmission
          testID="submit-button"
          atomicEventsAttributes={{
            domain: atomicsDomain,
            flow: atomicsFlows.guestAndSignup,
            intent: `click_${
              isGooglePayLaterOnly
                ? atomicsTasks.submitPayLaterOnly
                : atomicsTasks.submitPayLater
            }`,
            task: isGooglePayLaterOnly
              ? atomicsTasks.selectAgreeAndCreateAccountContinuePayLaterOnly
              : atomicsTasks.selectAgreeAndCreateAccountContinuePayLater,
            viewName: atomicsViewNames.reviewYourPayment,
          }}
        >
          {getContent('buttons.signup.base')}
        </Button>
      </form>
      {isResidentialAddressOpen && (
        <LazyResidentialAddressInterstitial
          submitHandler={payLaterSubmitHandler}
        />
      )}
    </>
  );
};
